import React, { useState, useEffect } from 'react';
import * as Flags from 'country-flag-icons/react/3x2';
import { Globe, Plus, Edit2, Trash2, Save, X } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Flag = ({ code, className }) => {
  const C = code ? Flags[code.toUpperCase()] : null;
  return C ? <C className={className} /> : <Globe className={className} />;
};

const emptyForm = { code: '', name: '', nativeName: '', countryCode: '', displayOrder: 0, isActive: true };

// Süperadmin: global dil kataloğu yönetimi (ekle/düzenle/pasifleştir).
const LanguagesManagement = () => {
  const [languages, setLanguages] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
  });

  const fetchLanguages = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/languages/all`, { headers: authHeaders() });
      if (res.ok) setLanguages(await res.json());
      else setError('Diller yüklenirken hata oluştu.');
    } catch (err) {
      setError('API bağlantı hatası.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLanguages();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const startCreate = () => {
    setForm(emptyForm);
    setIsCreating(true);
    setEditingId(null);
    setError('');
  };

  const startEdit = (lang) => {
    setForm({
      code: lang.code,
      name: lang.name || '',
      nativeName: lang.nativeName || '',
      countryCode: lang.countryCode || '',
      displayOrder: lang.displayOrder || 0,
      isActive: lang.isActive,
    });
    setEditingId(lang.id);
    setIsCreating(false);
    setError('');
  };

  const cancel = () => {
    setIsCreating(false);
    setEditingId(null);
    setForm(emptyForm);
    setError('');
  };

  const handleSave = async () => {
    if (!form.code.trim()) {
      setError('Dil kodu zorunludur (ör. tr, en).');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = { ...form, displayOrder: Number(form.displayOrder) || 0 };
      const res = editingId
        ? await fetch(`${API_BASE_URL}/api/languages/${editingId}`, {
            method: 'PUT',
            headers: authHeaders(),
            body: JSON.stringify({ ...payload, id: editingId }),
          })
        : await fetch(`${API_BASE_URL}/api/languages`, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify(payload),
          });

      if (res.ok) {
        await fetchLanguages();
        cancel();
      } else {
        setError('Kaydetme sırasında hata oluştu.');
      }
    } catch (err) {
      setError('API bağlantı hatası.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu dili pasifleştirmek istediğinize emin misiniz?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/languages/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.ok) await fetchLanguages();
      else setError('Silme sırasında hata oluştu.');
    } catch (err) {
      setError('API bağlantı hatası.');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200 p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Globe className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dil Kataloğu</h1>
              <p className="text-gray-600">Sistemde kullanılabilecek dilleri yönetin.</p>
            </div>
          </div>
          {!isCreating && !editingId && (
            <button
              onClick={startCreate}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Yeni Dil</span>
            </button>
          )}
        </div>

        {(isCreating || editingId) && (
          <div className="border-b border-gray-200 p-6 bg-gray-50">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{editingId ? 'Dili Düzenle' : 'Yeni Dil'}</h2>
            {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kod (ISO, ör. tr)</label>
                <input name="code" value={form.code} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ülke Kodu (bayrak, ör. TR)</label>
                <input name="countryCode" value={form.countryCode} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">İsim (İngilizce)</label>
                <input name="name" value={form.name} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Yerel İsim</label>
                <input name="nativeName" value={form.nativeName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sıra</label>
                <input type="number" name="displayOrder" value={form.displayOrder} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div className="flex items-center mt-6">
                <input id="isActive" type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} className="h-4 w-4 text-blue-600" />
                <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">Aktif</label>
              </div>
            </div>
            <div className="flex items-center space-x-3 mt-6">
              <button onClick={handleSave} disabled={loading} className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400">
                <Save className="h-4 w-4" />
                <span>{loading ? 'Kaydediliyor...' : 'Kaydet'}</span>
              </button>
              <button onClick={cancel} className="flex items-center space-x-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600">
                <X className="h-4 w-4" />
                <span>İptal</span>
              </button>
            </div>
          </div>
        )}

        <div className="p-6">
          {error && !isCreating && !editingId && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>
          )}
          {loading && languages.length === 0 ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : (
            <div className="space-y-2">
              {languages.map((lang) => (
                <div key={lang.id} className={`flex items-center gap-3 p-3 rounded-xl border ${lang.isActive ? 'border-gray-200' : 'border-gray-200 opacity-50'}`}>
                  <Flag code={lang.countryCode} className="h-5 w-7 rounded-sm object-cover flex-shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-gray-900 truncate">{lang.nativeName} <span className="text-gray-400">({lang.code})</span></span>
                    <span className="block text-xs text-gray-500 truncate">{lang.name}{!lang.isActive && ' • pasif'}</span>
                  </span>
                  <button onClick={() => startEdit(lang)} className="p-2 text-gray-400 hover:text-blue-600"><Edit2 className="h-4 w-4" /></button>
                  <button onClick={() => handleDelete(lang.id)} className="p-2 text-gray-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LanguagesManagement;
